import { useCallback } from 'react'
import {
  useRecoilValue,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil'
import {
  selectedItemAtom,
  itemSelector,
  itemAtomFamily,
  itemsLockedAtom,
  unsavedChangesAtom,
} from 'atoms'
import { Button, ColorPicker, Input, Clipboard } from 'components'
import Flex from 'components/priority/Flex/Flex'
import Text from 'components/priority/Text/Text'
import { useDuplicateItem } from 'hooks'
import { useEditorDuplicateTextBox } from '@/hooks/editor/useEditorDuplicateTextBox'
import { useEditorTextBoxDelete } from '@/hooks/editor/useEditorTextBoxDelete'
import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import { editorItemLockedSelector } from '@/atoms/editorTextBoxAtoms'
import {
  EDITOR_TEXT_SCALING_ENABLED,
  isEditorTextBoxItemType,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { LockAsset } from 'components/editor/PropertiesMenu/LockAsset'
import { HideInPdf } from '@/components/editor/PropertiesMenu/HideInPdf'
import { TextSizing } from '@/components/editor/PropertiesMenu/TextSizing'
import { useTranslation } from 'react-i18next'
import { revealWrapperHeightAtom } from '@/atoms/_editorAtoms'
import { ToggleSwitch } from '@/components/global/ToggleSwitch/ToggleSwitch'

export const TextBoxDrawer = () => {
  const { t } = useTranslation()
  const selectedItem = useRecoilValue(selectedItemAtom)
  const itemsLocked = useRecoilValue(itemsLockedAtom)
  // The width and height fields follow the item's lock, the one its resize
  // handles read.
  const itemLocked = useRecoilValue(editorItemLockedSelector(selectedItem))
  const [revealWrapperHeight, setRevealWrapperHeight] = useRecoilState(
    revealWrapperHeightAtom,
  )

  const type = useRecoilValue(itemSelector({ id: selectedItem, path: 'type' }))
  const isEditorTextBox = isEditorTextBoxItemType(type)

  const duplicateItem = useDuplicateItem()
  const duplicateEditorTextBox = useEditorDuplicateTextBox()
  const deleteEditorTextBox = useEditorTextBoxDelete()

  const [width, setWidth] = useRecoilState(
    itemSelector({ id: selectedItem, path: 'width' }),
  )
  const [height, setHeight] = useRecoilState(
    itemSelector({ id: selectedItem, path: 'height' }),
  )
  const [color, setColor] = useRecoilState(
    itemSelector({ id: selectedItem, path: 'edits.backgroundColor' }),
  )

  const resetItem = useResetRecoilState(itemAtomFamily(selectedItem))
  const resetControlState = useResetRecoilState(
    itemControlStateAtomFamily(selectedItem),
  )
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)

  const handleDuplicate = useCallback(() => {
    if (isEditorTextBox) void duplicateEditorTextBox(selectedItem)
    else duplicateItem()
  }, [isEditorTextBox, duplicateEditorTextBox, duplicateItem, selectedItem])

  const handleDelete = useCallback(() => {
    void deleteEditorTextBox(selectedItem)
    resetControlState()
    resetItem()
    setUnsavedChanges(true)
  }, [
    deleteEditorTextBox,
    selectedItem,
    resetControlState,
    resetItem,
    setUnsavedChanges,
  ])

  return (
    <Flex column padding="0 12px">
      <Button
        center
        margin="0 0 16px"
        onClick={handleDuplicate}
        disabled={itemsLocked}
        data-testid="drawer-duplicate-text"
      >
        {t(
          'editor.menus.properties.elements.textBoxDrawer.duplicateTextButton',
        )}
      </Button>
      <Button
        center
        margin="0 0 16px"
        onClick={handleDelete}
        data-testid="drawer-delete-text"
      >
        {t('editor.menus.properties.elements.textBoxDrawer.deleteTextButton')}
      </Button>
      <Clipboard />
      <LockAsset selectedItem={selectedItem} />
      <HideInPdf selectedItem={selectedItem} />
      <Text margin="0 0 16px">
        {t('editor.menus.properties.elements.textBoxDrawer.sizeText')}
      </Text>
      <Flex margin="0 0 24px">
        <Input
          value={[width, setWidth]}
          label={t('editor.menus.properties.elements.textBoxDrawer.widthLabel')}
          margin="0 16px 0 0"
          placeholder="100"
          type="number"
          disabled={itemLocked}
          data-testid="drawer-width"
        />
        <Input
          value={[height, setHeight]}
          label={t(
            'editor.menus.properties.elements.textBoxDrawer.heightLabel',
          )}
          placeholder={t(
            'editor.menus.properties.elements.textBoxDrawer.placeholder100',
          )}
          type="number"
          disabled={itemLocked}
          data-testid="drawer-height"
        />
      </Flex>
      {/* The "Max text size (pt)" field binds only a Fill text box, which
          exists only while the text-scaling switch is on, so the section
          shows only then. The maximum is a Lotus box's column; this drawer
          serves the classic text item too (`determineItemProperties`), whose
          autosize is shrink-only and has no row to carry it. */}
      {isEditorTextBox && EDITOR_TEXT_SCALING_ENABLED && (
        <TextSizing selectedItem={selectedItem} itemsLocked={itemsLocked} />
      )}
      <Flex margin="0 0 16px">
        <ToggleSwitch
          value={[revealWrapperHeight, setRevealWrapperHeight]}
          label={t(
            'editor.menus.properties.elements.textBoxDrawer.revealWrapperHeightLabel',
          )}
          labelRight
        />
      </Flex>
      {!isEditorTextBox && (
        <>
          <Text margin="0 0 16px">
            {t(
              'editor.menus.properties.elements.textBoxDrawer.textboxFillText',
            )}
          </Text>
          <ColorPicker
            label={t(
              'editor.menus.properties.elements.textBoxDrawer.colorLabel',
            )}
            margin="0 0 20px"
            color={[color, setColor]}
            resetButton
          />
        </>
      )}
    </Flex>
  )
}

export default TextBoxDrawer
